import { supabase } from '@/integrations/supabase/client';

/**
 * Fetches favorite escort profile IDs for a given user from Supabase.
 * @param userId - The ID of the user.
 * @returns A promise that resolves to an array of profile IDs.
 */
export const fetchFavorites = async (userId: string): Promise<string[]> => {
  console.log('Service: Fetching favorites for user:', userId);
  const { data, error } = await supabase
    .from('user_favorites')
    .select('profile_id')
    .eq('user_id', userId);

  if (error) {
    console.error('Service Error: Loading favorites from Supabase:', error);
    throw error; // Rethrow the error to be caught by the hook
  }
  
  const favoriteIds = data ? data.map(row => row.profile_id) : [];
  console.log('Service: Loaded favorites:', favoriteIds);
  return favoriteIds;
};

/**
 * Adds a favorite escort profile for a user in Supabase.
 * @param userId - The ID of the user.
 * @param profileId - The ID of the escort profile to add.
 * @returns A promise that resolves when the operation is complete.
 */
export const addFavorite = async (userId: string, profileId: string): Promise<void> => {
  console.log(`Service: Adding favorite ${profileId} for user ${userId}`);
  const { error } = await supabase
    .from('user_favorites')
    .insert({
      user_id: userId,
      profile_id: profileId
    });

  if (error) {
    console.error('Service Error: Adding favorite to Supabase:', error);
    // Handle potential unique constraint violation gracefully if needed
    if (error.code === '23505') { // Unique violation code
        console.warn(`Favorite ${profileId} already exists for user ${userId}.`);
        return; // Or rethrow specific error
    }
    throw error;
  }
  console.log(`Service: Favorite ${profileId} added successfully.`);
};

/**
 * Removes a favorite escort profile for a user from Supabase.
 * @param userId - The ID of the user.
 * @param profileId - The ID of the escort profile to remove.
 * @returns A promise that resolves when the operation is complete.
 */
export const removeFavorite = async (userId: string, profileId: string): Promise<void> => {
  console.log(`Service: Removing favorite ${profileId} for user ${userId}`);
  const { error } = await supabase
    .from('user_favorites')
    .delete()
    .eq('user_id', userId)
    .eq('profile_id', profileId);

  if (error) {
    console.error('Service Error: Removing favorite from Supabase:', error);
    throw error;
  }
   console.log(`Service: Favorite ${profileId} removed successfully.`);
};

/**
 * Migrates favorites from localStorage to Supabase.
 * @param userId - The ID of the user.
 * @param localFavoriteIds - An array of profile IDs from localStorage.
 * @returns A promise that resolves to true if migration was successful or partially successful, false otherwise.
 */
export const migrateLocalStorageFavorites = async (userId: string, localFavoriteIds: string[]): Promise<boolean> => {
  if (!localFavoriteIds || localFavoriteIds.length === 0) {
    return true; // Nothing to migrate
  }
  
  console.log(`Service: Migrating ${localFavoriteIds.length} favorites from localStorage for user ${userId}`);
  
  const rows = localFavoriteIds.map(profileId => ({
    user_id: userId,
    profile_id: profileId
  }));

  // Use upsert to avoid errors if some favorites already exist remotely
  const { error } = await supabase
    .from('user_favorites')
    .upsert(rows, { onConflict: 'user_id, profile_id' });

  if (error) {
    console.error('Service Error: Migrating favorites to Supabase:', error);
    return false; // Indicate migration failed
  }
  
  console.log('Service: Successfully migrated/upserted favorites to Supabase.');
  return true; // Indicate migration succeeded
}; 